import * as PIXI from 'pixi.js-legacy';
import MeasurementLine from './MeasurementLine';
import constants from '../settings/constants';
import tacHelpers from '../state/tac/tacHelpers';
import slowDevice from './util/slowDevice';
import platform from '../util/platform';
import getDepthOffset from './util/getDepthOffset';
import productService from '../services/products';
import { isFloorStanding, isWallMounted } from '../services/products/models';
import { ITEMS, RANGES } from '../constants';
import { applicationSettings } from '../settings/application';

const animationEnabled = !slowDevice();
const animationSpeed = 0.08;

export default class Measurements extends PIXI.Container {
  constructor({ app, isMobile, scene }) {
    super();
    this.app = app;
    this.isMobile = isMobile;
    this.scene = scene;
  }

  hasMeasurementsCovered(section) {
    const { tac } = this;
    if (productService.isExtendable(section)) {
      // we are already the smallest section, cannot be covered by others
      return false;
    }

    const superSection = tacHelpers.findSuperSection(tac, section);

    const others = superSection.filter(
      other =>
        productService.isSection(other) && other.itemid !== section.itemid
    );

    // first, let's make sure we have aligned sections on both ends
    const leftAligned = others.filter(other => other.x === section.x);

    if (!leftAligned.length) {
      return false;
    }

    const rightmostPoint = section.x + section.width;
    const rightAligned = others.filter(
      other => other.x + other.width === rightmostPoint
    );

    if (!rightAligned.length) {
      return false;
    }

    // we are aligned both left and right, now let's see if we are completely covered
    if (
      leftAligned.some(
        other =>
          other.x + other.width === section.x + section.width &&
          other.y < section.y
      )
    ) {
      // we have another equally sized section below us
      return true;
    }

    // finally, let's check that all our other positions in the grid are covered
    const stepInterval =
      constants.DYNAMIC_GRID?.[constants.DRAG_MODE.FLOAT].x.step ||
      constants.GRID?.x.step;
    for (let pos = section.x + stepInterval; pos < rightmostPoint; ) {
      if (
        !others.some(
          other =>
            other.width < section.width &&
            other.x <= pos &&
            other.x + other.width >= pos
        )
      ) {
        // ...they weren't
        return false;
      }
      pos += stepInterval;
    }

    // ...they were
    return true;
  }

  drawGapLines() {
    const { tac, ratio, room, origo, offset } = this;

    this.gapLines = this.gapLines || new PIXI.Container();

    if (!this.gapLines.parent) {
      this.addChild(this.gapLines);
    }

    this.gapLines &&
      this.gapLines.children.slice().forEach(child => {
        child.destroy();
      });

    const floorStanders = tac.items
      .filter(
        item =>
          (isFloorStanding(item) && (!isWallMounted(item) || item.y === 0)) ||
          productService.isType(item, 'sidewall')
      )
      .sort((a, b) => a.x - b.x);

    floorStanders.forEach((item, index, array) => {
      if (index === array.length - 1) {
        //no line on last item
        return;
      }

      const next = array[index + 1];

      const length = next.x - (item.x + item.width);

      if (length < 1) {
        // no line if no space between
        return;
      }

      if (
        productService.isType(item, 'sidewall') &&
        productService.isType(next, 'sidewall')
      ) {
        // no line between sidewalls in empty room
        return;
      }

      const line = new MeasurementLine({
        length,
        ratio,
        isMobile: this.isMobile,
      });

      const sprite = this.scene.itemContainer.getSprite(item.itemid);
      const { projectedVertices } = sprite.data;

      line.y =
        (room.height - item.y - projectedVertices[0][1]) * ratio + offset;
      line.x =
        (item.x + item.width + projectedVertices[0][0] - origo.x) * ratio - 2;

      line.itemRefs = {
        from: item.itemid,
        to: next.itemid,
      };

      this.gapLines.addChild(line);
    });
  }

  drawSectionLines() {
    const { tac, ratio, room, origo, offset } = this;

    const sections = tacHelpers.getSections(tac);

    this.sectionLines = this.sectionLines || new PIXI.Container();
    if (!this.sectionLines.parent) {
      this.addChild(this.sectionLines);
    }
    this.sectionLines &&
      this.sectionLines.children.slice().forEach(child => {
        child.destroy();
      });

    sections.forEach(section => {
      if (this.hasMeasurementsCovered(section)) {
        return;
      }
      const hasLeftNeighbour = sections.some(
        other => other.y === section.y && other.x + other.width === section.x
      );
      const sectionLine = new MeasurementLine({
        length: section.width,
        ratio,
        isMobile: this.isMobile,
        skipFirstSerif: hasLeftNeighbour,
      });

      sectionLine.y = (room.height - section.y) * ratio + offset;
      sectionLine.x = (section.x - origo.x) * ratio - 2; // is it the serifs width the reason we have to offset the line by 2px?

      this.sectionLines.addChild(sectionLine);
    });
  }

  /**
   * Instantiates, adds and positions the measurement lines for
   * tables in the configuration.
   * Lines whose label would not fully fit within the drawable area
   * are shifted to the right as much as necessary to prevent the
   * label from being cut off.
   */
  drawTableLines() {
    const { tac, ratio, room, origo, offset } = this;

    const tables = tacHelpers
      .getAllItems(tac.items)
      .filter(item => productService.isType(item, ITEMS.TABLE))
      .map(table => ({ ...table, ...tacHelpers.getGlobalCoords(table, tac) }));

    if (tables.length) {
      this.tableLines = this.tableLines || new PIXI.Container();

      if (!this.tableLines.parent) {
        this.addChild(this.tableLines);
      }
      this.tableLines &&
        this.tableLines.children.slice().forEach(child => {
          child.destroy();
        });

      tables.forEach(table => {
        const hasLeftNeighbour = tables.some(
          other => other.y === table.y && other.x + other.width === table.x
        );
        if (hasLeftNeighbour) {
          return;
        }
        const tableLine = new MeasurementLine({
          length: table.y + table.height,
          ratio,
          isMobile: this.isMobile,
          textInside: false,
          textUnderLine: false,
          isTable: true,
        });

        const sprite = this.scene.itemContainer.getSprite(table.itemid);
        const { projectedVertices } = sprite.data;

        tableLine.y =
          (room.height - table.y - table.height - projectedVertices[0][1]) *
          ratio;
        tableLine.x =
          (table.x - origo.x + projectedVertices[0][0]) * ratio - offset;

        this.tableLines.addChild(tableLine);

        const { x, y } = tableLine.getBounds();
        const tableLineUpperLeftWorld = new PIXI.Point(x, y);
        const tableLineUpperLeftLocal = this.toLocal(tableLineUpperLeftWorld);
        const drawableUpperLeftWorld = new PIXI.Point(0, 0);
        const drawableUpperLeftLocal = this.toLocal(drawableUpperLeftWorld);
        const shift = Math.max(
          0,
          drawableUpperLeftLocal.x - tableLineUpperLeftLocal.x
        );
        tableLine.x += shift;
      });
    }
  }

  drawClothesRailLines() {
    const { tac, ratio, room, origo, offset } = this;

    const extendableClothesRails = tacHelpers
      .getClothesRails(tac)
      .filter(productService.isMultiParentProduct);

    this.clothesRailLines = this.clothesRailLines || new PIXI.Container();
    if (!this.clothesRailLines.parent) {
      this.addChild(this.clothesRailLines);
    }

    this.clothesRailLines &&
      this.clothesRailLines.children.slice().forEach(child => {
        child.destroy();
      });

    extendableClothesRails.forEach((item, index) => {
      const currentLine = 'clothesRailLine_' + index;
      this[currentLine] && this[currentLine].destroy();

      const topAncestor = tacHelpers.getTopAncestor(tac, item);
      const connectsToTopAncestor = tacHelpers.getTopAncestor(
        tac,
        item.connectsTo
      );

      if (
        !connectsToTopAncestor ||
        this.gapLines.children.some(line => {
          return (
            line.itemRefs.from === topAncestor.itemid &&
            line.itemRefs.to === connectsToTopAncestor.itemid
          );
        })
      ) {
        // no line if this gap is already measured on floor
        return;
      }

      const length =
        connectsToTopAncestor.x - (topAncestor.x + topAncestor.width);

      this[currentLine] = new MeasurementLine({
        length,
        ratio: ratio,
        isMobile: this.isMobile,
        // FIXME: textInside is currently rendered the same way as textUnderLine.
        // Do we still need textInside? Consider changing to textUnderLine and
        // removing the textInside implementation from the MeasurementLine class.
        textInside: true,
      });

      const parentCoords = tacHelpers.getGlobalCoords(item, tac);
      const depthOffset = getDepthOffset(item);
      const sprite = this.scene.itemContainer.getSprite(item.itemid);

      const { projectedVertices } = sprite.data;

      this[currentLine].x =
        (topAncestor.x +
          topAncestor.width -
          depthOffset.x +
          projectedVertices[0][0] -
          origo.x) *
        ratio;
      this[currentLine].y =
        (room.height - parentCoords.y + item.height) * ratio + offset;

      this.clothesRailLines.addChild(this[currentLine]);
    });
  }

  getVerticalLineX(limits, size) {
    const { origo, ratio, offset, isPortrait } = this;
    const preferred = (limits.min.x + size.width - origo.x) * ratio + offset;
    if (
      isPortrait &&
      this.x + preferred + this.verticalLine.height >
        this.scene.room.targetWidth
    ) {
      return this.scene.room.targetWidth - this.verticalLine.height - this.x;
    }

    return preferred;
  }

  getDepthLineLength() {
    let depthLineLength = 0;
    this.scene.itemContainer.children.forEach(child => {
      if (
        child.data.outerSize.width - child.data.faceSize.width >
        depthLineLength
      ) {
        if (applicationSettings.applicationName !== RANGES.BOAXEL) {
          depthLineLength =
            child.data.outerSize.width - child.data.faceSize.width;
        } else if (child.childrenContainer.children.length > 0) {
          depthLineLength =
            child.data.outerSize.width - child.data.faceSize.width;
        }
      }
    });
    return depthLineLength;
  }

  getDepthText(size) {
    let depthText = 0;
    this.tac.items.forEach(item => {
      if (applicationSettings.applicationName === RANGES.BOAXEL) {
        depthText = size.depth;
      } else if (item.depth > depthText) {
        depthText = item.depth;
      }
    });
    return depthText;
  }

  draw() {
    const { tac, ratio, room, origo = { x: 0, y: 0 }, offset } = this;

    const limits = tacHelpers.getLimits(tac);
    const size = tacHelpers.getSize(tac);
    const depthLineLength = this.getDepthLineLength();

    this.horizontalLine && this.horizontalLine.destroy();
    this.verticalLine && this.verticalLine.destroy();
    this.depthLine && this.depthLine.destroy();
    this.tableLines &&
      this.tableLines.children.length &&
      this.tableLines.children.forEach(item => item.destroy());

    this.horizontalLine = new MeasurementLine({
      length: size.width,
      ratio: ratio,
      isMobile: this.isMobile,
    });
    this.horizontalLine.x = (limits.min.x - origo.x) * ratio;
    this.horizontalLine.y = (room.height - limits.max.y) * ratio - offset;

    this.addChild(this.horizontalLine);

    this.verticalLine = new MeasurementLine({
      length: size.height,
      ratio: ratio,
      isMobile: this.isMobile,
      isVertical: true,
    });
    this.verticalLine.x = this.getVerticalLineX(limits, size);
    this.verticalLine.y = (room.height - limits.max.y) * ratio;
    this.verticalLine.rotation = Math.PI / 2;

    this.addChild(this.verticalLine);

    this.depthLine = new MeasurementLine({
      length: depthLineLength,
      ratio: ratio,
      isMobile: this.isMobile,
      isDepth: true,
      size: this.getDepthText(size),
    });
    this.depthLine.x = this.getVerticalLineX(limits, size);
    this.depthLine.y = (room.height - limits.min.y) * ratio;

    this.addChild(this.depthLine);

    constants.DISPLAY_SECTION_MEASUREMENTS && this.drawSectionLines();
    this.drawGapLines();
    constants.DISPLAY_TABLE_MEASUREMENTS && this.drawTableLines();
    this.drawClothesRailLines();

    if (!this.active) {
      this.active = true;
      this.horizontalLine.alpha = 0;
      this.verticalLine.alpha = 0;
      this.depthLine.alpha = 0;
      this._fadeIn(this.verticalLine);
      this._fadeIn(this.horizontalLine);
      this._fadeIn(this.depthLine);

      [
        this.clothesRailLines,
        this.sectionLines,
        this.gapLines,
        this.tableLines,
      ].forEach(lineGroup => {
        lineGroup &&
          lineGroup.children.forEach(child => {
            child.alpha = 0;
            this._fadeIn(child);
          });
      });
    }
  }

  _fadeIn(sprite) {
    if (sprite.alpha < 1 && animationEnabled) {
      sprite.alpha += animationSpeed;
      if (this.active) {
        requestAnimationFrame(() => {
          this._fadeIn(sprite);
        });
        this.app.render();
      }
    } else {
      sprite.alpha = 1;
      this.app.render();
    }
  }

  clear() {
    if (
      this.verticalLine &&
      this.verticalLine.alpha > 0 &&
      animationEnabled &&
      !this.wallResizerActive
    ) {
      this.verticalLine.alpha -= animationSpeed;
      this.horizontalLine.alpha -= animationSpeed;
      this.depthLine.alpha -= animationSpeed;

      [
        this.clothesRailLines,
        this.sectionLines,
        this.gapLines,
        this.tableLines,
      ].forEach(lineGroup => {
        lineGroup &&
          lineGroup.children.forEach(child => {
            child.alpha -= animationSpeed;
          });
      });

      if (!this.active) {
        requestAnimationFrame(() => {
          this.clear();
        });
        if (this.app.renderer) {
          this.app.render();
        }
      }
    } else {
      this.verticalLine && this.verticalLine.destroy();
      this.verticalLine = null;

      this.horizontalLine && this.horizontalLine.destroy();
      this.horizontalLine = null;

      this.depthLine && this.depthLine.destroy();
      this.depthLine = null;

      [
        this.sectionLines,
        this.clothesRailLines,
        this.gapLines,
        this.tableLines,
      ].forEach(lineGroup => {
        lineGroup &&
          // need to slice these since destroying a child modifies children array
          lineGroup.children.slice().forEach(child => {
            child.destroy();
          });
      });
    }
  }

  update({
    active,
    wallResizerActive,
    origo,
    tac,
    room,
    ratio,
    isMobile,
    isPortrait,
  }) {
    this.isMobile = isMobile;
    this.wallResizerActive = wallResizerActive;
    this.origo = origo;
    this.tac = tac;
    this.room = room;
    this.ratio = ratio;
    this.isPortrait = isPortrait;

    this.offset = platform.isKiosk
      ? constants.MEASUREMENTS_OFFSET_KIOSK
      : isMobile
      ? constants.MEASUREMENTS_OFFSET_MOBILE
      : constants.MEASUREMENTS_OFFSET;

    this.offset += (constants.MEASUREMENTS_LINE_SERIF / 2) * ratio;

    if (active && tac.items.length > 0) {
      this.draw();
    } else if (this.verticalLine) {
      this.active = false;
      this.clear();
    }
  }
}
